import type { TextProps } from '@mantine/core';
import { Text } from '@mantine/core';
import { useInterval } from '@mantine/hooks';
import { memo, useEffect, useState } from 'react';

type CountdownTextProps = {
	expiresInSeconds: number;
	displayStyle?: 'humanized' | 'mm:ss';
	onExpire: () => void;
	prefix?: string;
	suffix?: string;
} & TextProps;

export const CountdownText = memo(
	({ expiresInSeconds, onExpire, displayStyle = 'humanized', prefix, suffix, ...textProps }: CountdownTextProps) => {
		const [seconds, setSeconds] = useState(expiresInSeconds);

		const interval = useInterval(() => {
			setSeconds(prev => {
				const next = prev - 1;
				if (next <= 0) {
					interval.stop();
					onExpire();
					return 0;
				}
				return next;
			});
		}, 1000);

		useEffect(() => {
			setSeconds(expiresInSeconds);
			interval.start();

			return () => {
				interval.stop();
			};
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [expiresInSeconds]);

		const minutes = Math.floor(seconds / 60);
		const remainingSeconds = seconds % 60;

		const timeText =
			displayStyle === 'mm:ss'
				? `${minutes}:${remainingSeconds.toString().padStart(2, '0')}`
				: seconds <= 60
					? `${seconds}s`
					: `${minutes}m ${remainingSeconds}s`;

		const displayText = `${prefix || ''}${timeText}${suffix || ''}`;

		return <Text {...textProps}>{displayText}</Text>;
	},
);
